import React, { useState, useEffect, useRef } from 'react';
import styles from './employee-work-day-popup.module.scss';
import { IEmployeeDaySchedule } from 'shared/api/booking/interfaces/calendar-day.type';
import { BookingApi } from 'shared/api';
import { businessStore } from 'entities/index';
import * as dateFns from 'date-fns';
import { Alert, DefaultButton } from 'shared/ui';
import { TimeRangePicker } from 'shared/ui/time-range-picker/time-range-picker';
import { DATE_FORMAT } from 'shared/consts/date-format';
import { useNavigate } from 'react-router-dom';
import { AppLinks } from 'app/router/consts/app-links.enum';

interface IEmployeeWorkDayPopupProps {
  employee: IEmployeeDaySchedule;
  date: Date;
  onClose: () => void;
  onUpdate: () => void;
  triggerCardRef?: HTMLElement | null;
}

const DEFAULT_START_TIME = '09:00';
const DEFAULT_END_TIME = '18:00';

export const EmployeeWorkDayPopup: React.FC<IEmployeeWorkDayPopupProps> = ({
  employee,
  date,
  onClose,
  onUpdate,
  triggerCardRef,
}) => {
  const navigate = useNavigate();
  const popupRef = useRef<HTMLDivElement>(null);
  const [timeRange, setTimeRange] = useState({
    start: employee.workDay?.startedAt || DEFAULT_START_TIME,
    end: employee.workDay?.finishedAt || DEFAULT_END_TIME,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);

  const bookingsCount = employee.bookings?.length || 0;

  const handleTypeClick = async (type: string) => {
    if (isLoading) return;
    if (type !== 'workDay' && bookingsCount > 0) {
      setAlertMessage(
        'У сотрудника есть активные записи на эту дату. Переместите их перед установкой типа дня'
      );
      return;
    }
    setIsLoading(true);
    try {
      const params: {
        userId: string;
        pointId: string;
        date: string;
        type: string;
        startedAt?: string;
        finishedAt?: string;
      } = {
        userId: employee.employee.id,
        pointId: businessStore.pointId || '',
        date: dateFns.format(date, DATE_FORMAT),
        type,
      };
      if (type === 'workDay') {
        params.startedAt = timeRange.start;
        params.finishedAt = timeRange.end;
      }
      await BookingApi.dayOverride(params);
      onUpdate();
      onClose();
    } catch (e) {
      setAlertMessage(
        e instanceof Error ? e.message : 'Ошибка при установке типа дня'
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (popupRef.current && popupRef.current.contains(target)) {
        return;
      }
      if (triggerCardRef) {
        if (triggerCardRef.contains(target)) {
          event.stopPropagation();
          return;
        }
      }
      onClose();
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, triggerCardRef]);

  const handleTimeRangeChange = (data: {
    dayIndex: number;
    timeRange: { start: string | null; end: string | null };
  }) => {
    setTimeRange({
      start: data.timeRange.start || DEFAULT_START_TIME,
      end: data.timeRange.end || DEFAULT_END_TIME,
    });
  };

  return (
    <>
      <div
        ref={popupRef}
        className={styles.popup}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={styles.popup__title}>
          {employee.employee.fio.split(' ')[1]}
        </div>

        <div className={styles.popup__field}>
          <div className={styles.popup__field_label}>рабочее время</div>
          <div className={styles.popup__time_row}>
            <TimeRangePicker
              index={0}
              timeRange={timeRange}
              timeInterval={10}
              onChange={handleTimeRangeChange}
            />
            <button
              className={styles.popup__confirm_btn}
              onClick={() => handleTypeClick('workDay')}
              disabled={isLoading}
              type="button"
            >
              <svg
                width="14"
                height="10"
                viewBox="0 0 14 10"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1 5L5 9L13 1"
                  stroke="#4caf50"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className={styles.popup__buttons}>
          <DefaultButton
            buttonText="График работы"
            onClick={() => {
              const path = AppLinks.EmployeeLink.replace(
                ':id',
                String(employee.employee.id)
              );
              navigate(path);
            }}
            color="white"
          />
          <DefaultButton
            buttonText="Выходной"
            onClick={() => handleTypeClick('dayOff')}
            disabled={isLoading}
            color="gray"
          />
          <DefaultButton
            buttonText="Отпуск"
            onClick={() => handleTypeClick('vacation')}
            disabled={isLoading}
            color="blue"
          />
          <DefaultButton
            buttonText="Административный"
            onClick={() => handleTypeClick('administrative')}
            disabled={isLoading}
            color="orange"
            className={styles.button_administrative}
          />
          <DefaultButton
            buttonText="Больничный"
            onClick={() => handleTypeClick('sickLeave')}
            disabled={isLoading}
            color="purple"
          />
        </div>
      </div>
      {alertMessage && (
        <Alert
          type="error"
          message={alertMessage}
          onClose={() => setAlertMessage(null)}
        />
      )}
    </>
  );
};
